import { Fragment, Suspense, useCallback, useMemo } from 'react';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
  Group as PanelGroup,
  Panel,
  Separator as PanelResizeHandle,
} from 'react-resizable-panels';
import { useHotKeys } from '../../hooks/useHotKeys';
import type { TreePanes } from '../../store/slices/pane';
import {
  selectActivePane,
  selectPropertyPaneVisible,
} from '../../store/slices/pane';
import { PANE_TARGETTREE } from '../../store/constants/panes';
import { selectPanes } from '../../store/selectors/pane/data';
import { PropertiesPane } from '../PropertiesPane/PropertiesPane';
import TreePane from '../TreePane/TreePane';
import { resetDirective } from '../../store/slices/treeDrag';
import { trackUIInteraction } from '../../utils/analyticsEvents';
import type { AppThunkDispatch } from '../../store';

import styles from './CatalogManager.module.scss';

const CatalogManager = function CatalogManager() {
  const dispatch = useDispatch<AppThunkDispatch>();
  const activePane = useSelector(selectActivePane);
  const panes = useSelector(selectPanes);

  const handleEscape = useCallback(() => {
    trackUIInteraction('hotkey_escape');
    dispatch(resetDirective());
  }, [dispatch]);

  const hotKeys = useMemo(() => ({ Escape: handleEscape }), [handleEscape]);

  useHotKeys(hotKeys);

  const propertiesPaneVisible = useSelector(selectPropertyPaneVisible);
  const showPropertiesPane = !!panes.length && propertiesPaneVisible;

  const defaultSize = useMemo(() => `${100 / panes.length}%`, [panes.length]);

  if (!panes.length) return null;

  return (
    <div className={styles.content}>
      <PanelGroup
        orientation="horizontal"
        id="catalog-panes"
        style={{ overflow: 'visible' }}
        className={cx(styles.trees, { [styles.collapsed]: showPropertiesPane })}
      >
        {panes.map(({ pane, catalog }, index) => {
          const active = activePane === pane;
          const collapsed = !!(activePane && !active);
          const openDetails = !showPropertiesPane && pane === PANE_TARGETTREE;

          if (!catalog || collapsed) {
            return null;
          }

          return (
            <Fragment key={pane}>
              <Panel
                id={`panel-${pane}`}
                style={{ overflow: 'visible' }}
                defaultSize={defaultSize}
                minSize="0%"
              >
                <TreePane
                  className={cx(styles.panel, { [styles.collapsed]: collapsed })}
                  active={active}
                  collapsed={collapsed}
                  catalog={catalog}
                  openDetails={openDetails}
                  pane={pane as TreePanes}
                />
              </Panel>
              {index < panes.length - 1 && (
                <PanelResizeHandle className={styles.resizeHandle} />
              )}
            </Fragment>
          );
        })}
      </PanelGroup>
      {showPropertiesPane && (
        <Suspense fallback={null}>
          <PropertiesPane className={styles.propertiesPanel} />
        </Suspense>
      )}
    </div>
  );
};

export default CatalogManager;
